import { compareDesc, format } from "date-fns";
import { groupBy } from "lodash-es";
import { useCallback, useEffect } from "react";

import { AI_PROMPT_STORAGE_KEY } from "../constants";
import { getDefaultMessage, sendAIChatQuery } from "../queries/chat-query";

import { AIAssistantDebugger, AIToolCallOutputItem } from "./AIAssistantDebugger";
import { AIChatToolbar } from "./AIChatToolbar";
import { StudentResponsesAllowedSwitch } from "./StudentResponsesAllowedSwitch";

import { AttachmentChip, ChatBubble, ChatCompose, ChatHistory } from "components/chat";
import { ContentSidebarHeader } from "components/materials/sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "components/materials/sidebar/ContentSidebarMain";
import { OverlayContainer } from "components/materials/sidebar/OverlayContainer";
import { AIServerFileType } from "components/server-types";
import { getSidebarHeader } from "helpers/sidebar";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { LoadingDotsIcon, RobotAIIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectAiChatMessages, selectIsStudentResponsesForAIEnabled } from "store/selectors";
import { AIChatMessage, AIToolResponseMessage, aiStateActions } from "store/slices/ai";
import { useListSelector } from "store/store-hooks";
import { removeBGColorOnCopy } from "utils/copy";
import { CONTENT_SIDEBAR_TAB_NAME } from "utils/urls";
import { isAiDebuggerVisible } from "utils/waffle-flags";

export const AIChatPanel = () => {
  const dispatch = useAppDispatch();

  const { canParticipateInSection: isStudent } = useCapabilities();
  const isStudentResponsesForAIEnabled = useAppSelector(selectIsStudentResponsesForAIEnabled);
  // Message render path reads union-specific fields (files, tool args/output, etc.).
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const messages = useListSelector(selectAiChatMessages);
  const canSeeAIDebugger = isAiDebuggerVisible();

  const sortedMessages = messages.toSorted((a, b) => {
    return compareDesc(a.createdAt, b.createdAt);
  });
  const messagesByDate = groupBy(sortedMessages, (message) =>
    format(message.createdAt, "yyyy-MM-dd"),
  );

  // Initialize with default message if there are no messages
  useEffect(() => {
    if (!messages || messages.length === 0) {
      dispatch(
        aiStateActions.setAiChatMessage(
          getDefaultMessage(isStudent, isStudentResponsesForAIEnabled),
        ),
      );
    }
  }, [messages, dispatch, isStudent, isStudentResponsesForAIEnabled]);

  const handleAIQuery = useCallback(
    (inputText: string, files?: AIServerFileType[]) => {
      if (!inputText && !files?.length) {
        return;
      }

      sendAIChatQuery(dispatch, inputText, files);
    },
    [dispatch],
  );

  return (
    <OverlayContainer className="bg-white">
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <IconText
          iconStart={<RobotAIIcon className="text-blue" />}
          text={getSidebarHeader(CONTENT_SIDEBAR_TAB_NAME.AiAssistant)}
        />

        <AIChatToolbar />
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false}>
        <div className="flex h-full flex-col gap-2">
          <StudentResponsesAllowedSwitch />

          {canSeeAIDebugger && <AIAssistantDebugger />}

          <ChatHistory
            className="min-h-0 grow overflow-y-auto overflow-x-hidden"
            messagesByDate={Object.entries(messagesByDate)}
            renderMessage={(message: AIChatMessage, ref) => {
              if (message.type === "function_call") {
                const outputMessage = messages.find(
                  (m) => m.type === "function_call_output" && m.call_id === message.call_id,
                ) as AIToolResponseMessage;
                return (
                  <div key={`${message.id}-container`}>
                    <AIToolCallOutputItem
                      functionCall={message}
                      functionCallOutput={outputMessage}
                    />
                    {message.text && (
                      <ChatBubble ref={ref} parseMarkdown>
                        {message.text}
                        {/* {!outputMessage || (!outputMessage.finishedAt && <LoadingDotsIcon />)} */}
                      </ChatBubble>
                    )}
                  </div>
                );
              }
              if (message.type === "function_call_output") {
                // This is handled by AIToolCallOutputItem above, which rolls up call and output messages
                // into one (if available).
                return null;
              }
              if (message.role === "user") {
                return (
                  <ChatBubble key={message.id} ref={ref} isSelf parseMarkdown>
                    {message.files && message.files.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1.5">
                        {message.files.map((file) => (
                          <AttachmentChip
                            contentType={file.content_type}
                            key={file.url}
                            name={file.name ?? "File"}
                            url={file.url}
                          />
                        ))}
                      </div>
                    )}
                    {message.text}
                  </ChatBubble>
                );
              }
              return (
                <ChatBubble key={message.id} ref={ref} parseMarkdown>
                  {message.text}
                  {message.isLoading && <LoadingDotsIcon />}
                </ChatBubble>
              );
            }}
            onCopy={removeBGColorOnCopy}
          />

          <ChatCompose
            context="ai"
            persistKey={AI_PROMPT_STORAGE_KEY}
            placeholder={t("chat.send_a_message_ai")}
            onSend={(text, files) => handleAIQuery(text, files)}
          />
        </div>
      </ContentSidebarMain>
    </OverlayContainer>
  );
};
